import { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../AuthContext';

function ResetPassword() {
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [tokenVerified, setTokenVerified] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  const { supabase, setIsResettingPassword } = useAuth();

  useEffect(() => {
    if (tokenVerified) {
      setError(null); // Clear any prior error when verified
    }
  }, [tokenVerified]);

  useEffect(() => {
    setIsResettingPassword(true); // Skip auth fetches

    const { data: authListener } = supabase.auth.onAuthStateChange((event, session) => {
      console.log('Auth event:', event); // Debug
      if (event === 'PASSWORD_RECOVERY') {
        setTokenVerified(true);
        setSuccess('Recovery link verified. Please enter your new password.');
      } else if (event === 'SIGNED_IN') {
        // Optional: Handle if signed in prematurely
      }
    });

    // Parse URL params from both hash and search
    const parseAndSetSession = async () => {
      console.log('Full URL:', window.location.href); // Debug
      let rawParams = location.hash.substring(1) || location.search.substring(1);
      if (rawParams) {
        const params = new URLSearchParams(rawParams);
        console.log('Parsed params:', Object.fromEntries(params));
        const type = params.get('type');
        if (type === 'recovery') {
          const accessToken = params.get('access_token');
          const refreshToken = params.get('refresh_token');
          if (accessToken && refreshToken) {
            try {
              const { error } = await supabase.auth.setSession({ access_token: accessToken, refresh_token: refreshToken });
              if (error) throw error;
              // The onAuthStateChange should catch PASSWORD_RECOVERY now
            } catch (err) {
              setError(`Failed to set recovery session: ${err.message}`);
            }
          } else {
            setError('Invalid recovery link: Missing tokens.');
          }
        } else {
          setError('No valid recovery link detected. Please request a new reset.');
        }
      } else {
        setError('No recovery parameters found in URL. Try pasting the link in this tab.');
      }
    };

    parseAndSetSession();

    return () => {
      authListener.subscription.unsubscribe();
      setIsResettingPassword(false);
    };
  }, [supabase, location, setIsResettingPassword]);

  const handleResetPassword = async (e) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);
    if (!newPassword || newPassword !== confirmPassword) {
      setError('Passwords must match and cannot be empty.');
      return;
    }
    if (newPassword.length < 6) {
      setError('Password must be at least 6 characters long.');
      return;
    }
    setLoading(true);
    try {
      const { error: updateError } = await supabase.auth.updateUser({ password: newPassword });
      if (updateError) {
        let errorMsg = `Password update failed: ${updateError.message}`;
        if (updateError.message.includes('session missing') || updateError.message.includes('token expired')) {
          errorMsg = 'Recovery session expired. Please request a new reset link.';
        } else if (updateError.message.includes('rate limit')) {
          errorMsg = 'Rate limit exceeded. Please wait a few minutes and try again.';
        }
        throw new Error(errorMsg);
      }

      await supabase.auth.signOut(); // Clear session for fresh sign-in
      setSuccess('Password reset successfully! Redirecting to sign in...');
      setTimeout(() => navigate('/signin'), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex flex-col bg-gray-100">
      <div className="flex-grow p-4">
        <div className="max-w-md mx-auto bg-white p-6 rounded-lg shadow-md">
          <h2 className="text-2xl font-bold mb-4 text-center">Reset Password</h2>
          {error && <p className="text-red-500 mb-4 text-center">{error}</p>}
          {success && <p className="text-green-500 mb-4 text-center">{success}</p>}
          {loading ? (
            <p className="text-center text-gray-700">Processing...</p>
          ) : tokenVerified ? (
            <form onSubmit={handleResetPassword} className="space-y-4">
              <div>
                <label className="block mb-1 text-gray-700">New Password</label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={(e) => setNewPassword(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <div>
                <label className="block mb-1 text-gray-700">Confirm New Password</label>
                <input
                  type="password"
                  value={confirmPassword}
                  onChange={(e) => setConfirmPassword(e.target.value)}
                  className="w-full p-2 border border-gray-300 rounded-lg"
                  required
                />
              </div>
              <button
                type="submit"
                className="w-full bg-blue-500 text-white p-2 rounded-lg hover:bg-blue-600 disabled:bg-gray-400"
                disabled={loading}
              >
                {loading ? 'Resetting...' : 'Reset Password'}
              </button>
            </form>
          ) : (
            <p className="text-center text-gray-700">Verifying recovery link... (If issues, paste link in this tab)</p>
          )}
        </div>
      </div>
    </div>
  );
}

export default ResetPassword;